import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useProposalBlueprint from "./useProposalBlueprint";

// Step state and navigation for the proposal wizard, driven from this page's
// own Previous/Next/Finish buttons rather than the ones @craft-form/renderer
// draws (CraftProposalForm passes showNavButtons={false} and feeds
// activeStep/setActiveStep back into the renderer, which supports being
// step-controlled by its host).
//
// This mirrors the renderer's own dynamic-wizard nav semantics for a
// "saveMode": "final" wizard, which is what the published layout declares:
//   - Next     -> validate ONLY the current step's fields, then advance. No
//                 save call; intermediate steps never persist.
//   - Finish   -> handleSubmit, which validates the WHOLE form before saving.
//   - Previous -> step back, no validation.
//
// Steps and their per-step field names come from useProposalBlueprint, which
// reads the DB-published layout and normalises both step spellings (a `wizard`
// node's `steps` and a wizardMode `tabs` node's `tabs`).
//
// Deliberately NOT filtering out rule-hidden fields: the renderer's own
// wizard doesn't either, and its rules engine only toggles a node's `hide`
// flag rather than removing it from the step tree — so collecting names from
// the raw layout yields the same list the renderer would validate. A hidden
// field is never rendered, so it never registers with react-hook-form and
// trigger() passes over it.
const useProposalWizard = (methods, onFinish) => {
  const { steps, stepFieldNames } = useProposalBlueprint();

  const [activeStep, setRawActiveStep] = useState(0);

  // Every forward move (Next, or clicking a later tab header) goes through
  // here, since the renderer is step-controlled by this setter. Moving forward
  // requires every step BEFORE the target to be valid — so a user can't skip
  // an unfilled page, nor fill page 1, advance, go back, clear it, and then
  // click a later tab header. On failure it lands on the first invalid step,
  // where trigger() has already put the inline errors on screen. Moving back
  // is never validated.
  const setActiveStep = useCallback(
    async (target) => {
      const next = typeof target === "function" ? target(activeStep) : target;
      if (next <= activeStep) {
        setRawActiveStep(next);
        return true;
      }
      for (let step = 0; step < next; step += 1) {
        const isStepValid = await methods.trigger(stepFieldNames[step] || []);
        if (!isStepValid) {
          setRawActiveStep(step);
          toast.error("Please fill all required fields on this page before continuing.");
          return false;
        }
      }
      setRawActiveStep(next);
      return true;
    },
    [methods, stepFieldNames, activeStep],
  );

  const isFirstStep = activeStep === 0;
  // Guarded on length so an unloaded blueprint (steps === []) can't make
  // `activeStep === -1` read as "on the last step".
  const isLastStep = steps.length > 0 && activeStep === steps.length - 1;

  const goNext = useCallback(async () => {
    if (steps.length === 0) return;
    await setActiveStep(Math.min(activeStep + 1, steps.length - 1));
  }, [setActiveStep, activeStep, steps.length]);

  const goBack = useCallback(() => {
    setRawActiveStep((step) => Math.max(step - 1, 0));
  }, []);

  // Validates every step's fields before onFinish sees the values.
  const finish = useMemo(
    () => methods.handleSubmit(onFinish),
    [methods, onFinish],
  );

  return {
    activeStep,
    setActiveStep,
    isFirstStep,
    isLastStep,
    goNext,
    goBack,
    finish,
  };
};

export default useProposalWizard;
